/**
 * Which notifications go out, and what they say.
 *
 * One screen for two decisions, because they are asked about together: someone
 * looking at "paper printed" wants to switch it off *or* reword it, and making
 * those two different pages means finding the same list twice.
 *
 * Used at both scopes. At school scope every control is driven by what the
 * server says the school may do - `enabled_on_site`, `enabled_for_school`,
 * `can_customise`, `switchable` - and never by a client-side role check, so a
 * school administrator is told "TeachCraftAI has turned this off" rather than
 * being shown a switch that springs back.
 *
 * The switches save on change with no Save button, following AiCapabilities: a
 * toggle that waits for a second click reads as broken. The wording is a
 * draft-and-save form, because half-typed prose must not be what gets sent.
 */

import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import type { EmailEvent } from '../../lib/api/client'
import {
  Button,
  Checkbox,
  ConfirmButton,
  Field,
  Input,
  Pill,
  Spinner,
  Textarea,
} from '../../components/ui'
import { Icon } from '../../components/icons'
import { toast } from '../../components/toast'
import './email.css'

const CATEGORY_LABELS: Record<string, string> = {
  workflow: 'Paper workflow',
  account: 'Accounts and access',
}

/**
 * The API calls this screen makes, chosen once by scope.
 *
 * A scope object rather than `schoolId ? a() : b()` at every call site - the
 * two scopes differ only in which endpoints they hit, and threading a flag
 * through each mutation is how the two drift apart.
 */
function scopeFor(schoolId: string | undefined, isSchool: boolean) {
  const key = isSchool ? ['school', 'email', schoolId ?? 'current'] : ['platform', 'email']
  return {
    isSchool,
    eventsKey: [...key, 'events'],
    templateKey: (event: string) => [...key, 'template', event],
    listEvents: () => (isSchool ? api.schoolEmailEvents(schoolId) : api.emailEvents()),
    setEnabled: (event: string, enabled: boolean | null) =>
      isSchool
        ? api.setSchoolEmailEventEnabled(event, enabled, schoolId)
        : api.setEmailEventEnabled(event, enabled ?? false),
    getTemplate: (event: string) =>
      isSchool ? api.schoolEmailTemplate(event, schoolId) : api.emailTemplate(event),
    saveTemplate: (event: string, body: { subject: string; html_body: string; text_body: string }) =>
      isSchool
        ? api.updateSchoolEmailTemplate(event, body, schoolId)
        : api.updateEmailTemplate(event, body),
    resetTemplate: (event: string) =>
      isSchool ? api.resetSchoolEmailTemplate(event, schoolId) : api.resetEmailTemplate(event),
    preview: (event: string) =>
      isSchool ? api.previewSchoolEmailTemplate(event, schoolId) : api.previewEmailTemplate(event),
  }
}

type Scope = ReturnType<typeof scopeFor>

export function EmailTemplates({
  schoolId,
  isSchool = false,
}: {
  schoolId?: string
  isSchool?: boolean
}) {
  const scope = scopeFor(schoolId, isSchool)
  const events = useQuery({ queryKey: scope.eventsKey, queryFn: scope.listEvents })
  const [selected, setSelected] = useState<string | null>(null)

  if (events.isLoading) return <Spinner label="Loading notifications" />
  if (!events.data) return null

  const categories = [...new Set(events.data.map((event) => event.category))]

  return (
    <section className="tc-email-panel" aria-labelledby="email-events-title">
      <header className="tc-email-heading">
        <span className="tc-email-icon" aria-hidden="true">
          <Icon.review size={20} />
        </span>
        <div>
          <span className="tc-email-eyebrow">{isSchool ? 'School policy' : 'Platform policy'}</span>
          <h2 id="email-events-title">Notifications</h2>
          <p>
            {isSchool
              ? 'What your school sends, and what it says. Anything TeachCraftAI has switched off cannot be turned back on here.'
              : 'Choose which notifications go out, and what they say. Switching one off stops it for every school.'}
          </p>
        </div>
        <Pill tone="secondary">{events.data.filter((e) => e.enabled).length} on</Pill>
      </header>

      <div className="tc-email-body">
        {categories.map((category) => (
          <div key={category} className="tc-email-group">
            <h3>{CATEGORY_LABELS[category] ?? category}</h3>
            <ul className="tc-email-events">
              {events.data
                .filter((event) => event.category === category)
                .map((event) => (
                  <EventRow
                    key={event.key}
                    event={event}
                    scope={scope}
                    open={selected === event.key}
                    onToggleOpen={() =>
                      setSelected((current) => (current === event.key ? null : event.key))
                    }
                  />
                ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  )
}

function EventRow({
  event,
  scope,
  open,
  onToggleOpen,
}: {
  event: EmailEvent
  scope: Scope
  open: boolean
  onToggleOpen: () => void
}) {
  const queryClient = useQueryClient()
  const setEnabled = useMutation({
    mutationFn: (enabled: boolean | null) => scope.setEnabled(event.key, enabled),
    // Written straight into the cache rather than refetched: a switch that
    // flicks back to its old position for half a second reads as a failure.
    onSuccess: (updated) =>
      queryClient.setQueryData(scope.eventsKey, (current: EmailEvent[] | undefined) =>
        current?.map((row) => (row.key === updated.key ? updated : row)),
      ),
    onError: (error: Error) => toast(error.message, 'error'),
  })

  // Off at the platform means a school cannot turn it back on, and is told so
  // rather than being shown a switch that refuses to move.
  const blockedBySite = scope.isSchool && !event.enabled_on_site
  const locked = blockedBySite || (scope.isSchool && !event.can_customise)
  const inheriting = scope.isSchool && event.enabled_for_school === null

  return (
    <li className={`tc-email-event ${event.enabled ? '' : 'is-off'}`}>
      <div className="tc-email-event-main">
        <div className="tc-email-event-copy">
          <strong>{event.label}</strong>
          <span>{event.description}</span>
          {blockedBySite && (
            <span className="tc-email-event-note">Turned off by TeachCraftAI</span>
          )}
          {!blockedBySite && locked && (
            <span className="tc-email-event-note">Following TeachCraftAI</span>
          )}
        </div>
        <div className="tc-email-event-controls">
          {event.customised && <Pill tone="secondary">Edited</Pill>}
          {inheriting && !locked && <Pill tone="muted">Following platform</Pill>}
          {event.switchable ? (
            <Checkbox
              label="Send this"
              checked={event.enabled}
              disabled={locked || setEnabled.isPending}
              onChange={(e) => setEnabled.mutate(e.target.checked)}
            />
          ) : (
            /* Not a disabled switch: a control that cannot move should not look
               like one that is merely unavailable right now. */
            <Pill tone="success">Always sent</Pill>
          )}
          {scope.isSchool && !inheriting && !locked && event.switchable && (
            <Button variant="ghost" size="sm" onClick={() => setEnabled.mutate(null)}>
              Follow platform
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleOpen}
            aria-expanded={open}
            icon={open ? <Icon.collapse size={15} /> : <Icon.edit size={15} />}
          >
            {open ? 'Close' : locked ? 'View wording' : 'Edit wording'}
          </Button>
        </div>
      </div>
      {open && <TemplateEditor event={event} scope={scope} readOnly={locked} />}
    </li>
  )
}

function TemplateEditor({
  event,
  scope,
  readOnly,
}: {
  event: EmailEvent
  scope: Scope
  readOnly: boolean
}) {
  const template = useQuery({
    queryKey: scope.templateKey(event.key),
    queryFn: () => scope.getTemplate(event.key),
  })

  if (template.isLoading) return <Spinner label="Loading wording" />
  if (!template.data) return null

  return (
    <TemplateForm
      key={JSON.stringify(template.data)}
      event={event}
      scope={scope}
      readOnly={readOnly}
      saved={template.data}
    />
  )
}

type Draft = { subject: string; html_body: string; text_body: string }

function TemplateForm({
  event,
  scope,
  readOnly,
  saved,
}: {
  event: EmailEvent
  scope: Scope
  readOnly: boolean
  saved: Draft & { event_key: string }
}) {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Draft | null>(null)
  const [preview, setPreview] = useState<Draft | null>(null)
  const lastFocused = useRef<'subject' | 'html_body' | 'text_body'>('subject')

  const value = draft ?? {
    subject: saved.subject,
    html_body: saved.html_body,
    text_body: saved.text_body,
  }
  const set = (key: keyof Draft, next: string) => setDraft({ ...value, [key]: next })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: scope.templateKey(event.key) })
    queryClient.invalidateQueries({ queryKey: scope.eventsKey })
  }

  const save = useMutation({
    mutationFn: () => scope.saveTemplate(event.key, value),
    onSuccess: () => {
      setDraft(null)
      setPreview(null)
      invalidate()
      toast(`"${event.label}" wording saved.`)
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const revert = useMutation({
    mutationFn: () => scope.resetTemplate(event.key),
    onSuccess: () => {
      setDraft(null)
      setPreview(null)
      invalidate()
      toast(
        scope.isSchool
          ? `"${event.label}" is back to the TeachCraftAI wording.`
          : `"${event.label}" is back to the built-in wording.`,
      )
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const showPreview = useMutation({
    mutationFn: () => scope.preview(event.key),
    onSuccess: (result) => setPreview(result),
    onError: (error: Error) => toast(error.message, 'error'),
  })

  /** Drop a placeholder where the cursor was, rather than making people type it. */
  const insert = (name: string) => {
    const field = lastFocused.current
    setDraft({ ...value, [field]: `${value[field]}{{ ${name} }}` })
  }

  return (
    <div className="tc-email-editor">
      {readOnly ? (
        <p className="tc-email-note">
          This is the wording TeachCraftAI sends. Your school has not been given its own.
        </p>
      ) : (
        <div className="tc-email-placeholders">
          <span>Insert:</span>
          {event.placeholders.map((name) => (
            <button key={name} type="button" className="tc-email-chip" onClick={() => insert(name)}>
              {`{{ ${name} }}`}
            </button>
          ))}
        </div>
      )}

      <Field label="Subject">
        <Input
          value={value.subject}
          readOnly={readOnly}
          onFocus={() => (lastFocused.current = 'subject')}
          onChange={(e) => set('subject', e.target.value)}
        />
      </Field>
      <Field
        label="HTML message"
        hint="What most people will see. Inline styles only — email clients ignore stylesheets."
      >
        <Textarea
          rows={9}
          value={value.html_body}
          readOnly={readOnly}
          onFocus={() => (lastFocused.current = 'html_body')}
          onChange={(e) => set('html_body', e.target.value)}
        />
      </Field>
      <Field
        label="Plain-text message"
        hint="Sent alongside the HTML, for text-only clients. Leave blank and one is derived from the HTML."
      >
        <Textarea
          rows={7}
          value={value.text_body}
          readOnly={readOnly}
          onFocus={() => (lastFocused.current = 'text_body')}
          onChange={(e) => set('text_body', e.target.value)}
        />
      </Field>

      <div className="tc-email-actions">
        {!readOnly && (
          <>
            <Button
              variant="primary"
              disabled={!draft || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? 'Saving…' : 'Save wording'}
            </Button>
            <Button variant="secondary" disabled={!draft} onClick={() => setDraft(null)}>
              Discard changes
            </Button>
          </>
        )}
        <Button
          variant="ghost"
          disabled={!!draft || showPreview.isPending}
          onClick={() => showPreview.mutate()}
          title={draft ? 'Save your changes to preview them.' : undefined}
        >
          {showPreview.isPending ? 'Building…' : 'Preview'}
        </Button>
        {!readOnly && event.customised && (
          <ConfirmButton
            label={scope.isSchool ? 'Use TeachCraftAI wording' : 'Revert to default'}
            confirmLabel="Yes, revert"
            message="The wording underneath comes back. The on/off switch is not changed."
            onConfirm={() => revert.mutate()}
          />
        )}
      </div>

      {preview && (
        <div className="tc-email-preview">
          <h4>Preview</h4>
          <p className="tc-email-preview-subject">{preview.subject}</p>
          {/*
            A sandboxed iframe rather than dangerouslySetInnerHTML. The body is
            markup somebody typed into a settings box, and a site administrator
            previewing a school's template would otherwise be running that
            school's markup in their own session - an `<img onerror>` is all it
            takes. `sandbox` with no allow-scripts means nothing in here runs,
            and it renders the message in its own box like a mail client would.
          */}
          <iframe
            className="tc-email-preview-frame"
            title={`Preview of ${event.label}`}
            sandbox=""
            srcDoc={preview.html_body}
          />
        </div>
      )}
    </div>
  )
}
